import React, { Component } from 'react';
import { APP_CONST } from '../../constants';
import { GeckoCard, GeckoAlert } from '../../components';
import { $state } from '../../router';
import { Auth, TokenManager } from '../../services';

// Account Modules
import ModuleBasic from './components/ModuleBasic';
import ModulePassword from './components/ModulePassword';
import ModuleTwoFactor from './components/ModuleTwoFactor';
import ModuleActivity from './components/ModuleActivity';
import ModuleActiveSessions from './components/ModuleActiveSessions';
import ModuleApiTokens from './components/ModuleApiTokens';

export class AccountPage extends Component {
    state = {
        activeTab: 'AccountDetails'
    }

    constructor(props) {
        super(props);

        this.setAlert = this.setAlert.bind(this);
        this.iam = this.iam.bind(this);
        this.setActiveTab = this.setActiveTab.bind(this);
    }

    modules = [{
        key: 'AccountDetails',
        title: 'Account details',
        components: [ModuleBasic, ModulePassword],
        hidePasswordWhen: () => {
            return this.isSSOUser();
        },
    }, {
        key: 'TwoFactor',
        title: 'Two factor authentication',
        component: ModuleTwoFactor,
        hideWhen: () => {
            return this.isSSOUser();
        },
    }, {
        key: 'ActiveSessions',
        title: 'Active sessions',
        component: ModuleActiveSessions,
    }, {
        key: 'Activity',
        title: 'Activity',
        component: ModuleActivity,
    }, {
        key: 'ApiTokens',
        title: 'API tokens',
        component: ModuleApiTokens,
    },];

    isSSOUser() {
        // SSO users cant change their password.
        const tm = TokenManager.fromLocalStorage();
        const IdJwt = tm.IdToken;
        const IdToken = TokenManager.decode(IdJwt);

        if (IdToken.identities) {
            return true;
        }

        return false;
    }

    /* ====================

        Lifcycle

    ==================== */

    componentDidMount() {
        this.iam();
        
        const visibleModules = this.getVisibleModules();
        if (visibleModules.length > 0 && !visibleModules.find(m => m.key === this.state.activeTab)) {
            this.setState({ activeTab: visibleModules[0].key });
        }
    }


    /* ====================

        Global Methods

    ==================== */
    logout() {
        return $state.go('logout');
    }

    iam() {
        return Auth.iam().then((data) => {
            this.setState({
                user: data,
            })
        })
        .catch((err) => {
            this.setState({ alert: err.Error });
        })
    }

    setAlert(alert) {
        this.setState({
            alert: alert,
        })
    }

    setActiveTab(tabKey) {
        this.setState({
            activeTab: tabKey
        })
    }

    /* ====================

        Renders

    ==================== */

    getVisibleModules() {
        return this.modules.filter((module) => {
            if (module.hideWhen && module.hideWhen() === true) {
                return false;
            }
            return true;
        });
    }

    shouldShowPasswordModule() {
        const { activeTab } = this.state;
        const module = this.modules.find(m => m.key === activeTab);
        
        if (module && module.key === 'AccountDetails') {
            if (module.hidePasswordWhen && module.hidePasswordWhen() === true) {
                return false;
            }
            return true;
        }
        return false;
    }


    getUserFirstName() {
        if (!this.state.user || !this.state.user.FirstName) {
            return null;
        }
        return this.state.user.FirstName;
    }

    renderGreeting() {
        const firstName = this.getUserFirstName();
        if (!firstName) {
            return null;
        }

        return (
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-foreground mb-1">
                    Hi {firstName} 👋
                </h3>
                <p className="text-xs text-muted-foreground">
                    Manage your account settings, security preferences, and view your activity history.
                </p>
            </div>
        );
    }

    renderNavigation() {
        const visibleModules = this.getVisibleModules();
        const { activeTab } = this.state;

        return (
            <nav aria-label="Account navigation" className="flex flex-1 flex-col">
                {this.renderGreeting()}
                <ul role="list" className="space-y-[1px]">
                    {visibleModules.map((module) => {
                        const isActive = activeTab === module.key;
                        const baseClasses = 'group flex gap-x-3 rounded-sm p-2 pl-3 text-sm/6 font-medium cursor-pointer transition-colors';
                        const activeClasses = 'bg-slate-50 text-foreground';
                        const inactiveClasses = 'text-muted-foreground hover:bg-slate-100 hover:text-foreground';

                        return (
                            <li key={module.key}>
                                <button
                                    type="button"
                                    onClick={() => this.setActiveTab(module.key)}
                                    className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses} focus-visible:outline-2 focus-visible:outline-slate-500 focus-visible:bg-slate-50 -outline-offset-2 w-full`}
                                >
                                    {module.title}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </nav>
        );
    }

    renderActiveModule() {
        const { activeTab } = this.state;
        const module = this.modules.find(m => m.key === activeTab);

        if (!module || (module.hideWhen && module.hideWhen() === true)) {
            const visibleModules = this.getVisibleModules();
            if (visibleModules.length > 0) {
                const fallbackModule = visibleModules[0];
                return this.renderModule(fallbackModule);
            }
            return null;
        }

        if (module.components && Array.isArray(module.components)) {
            if (module.key === 'AccountDetails') {
                return this.renderAccountDetailsTab(module);
            }
        }

        return this.renderModule(module);
    }

    renderAccountDetailsTab(module) {
        const showPassword = this.shouldShowPasswordModule();

        return (
            <div key={module.key} id={'module-' + module.key} className="flex flex-col gap-6">
                <div className="flex flex-col">
                    <h2 className="text-2xl font-semibold text-foreground">
                        {module.title}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Manage your personal information and account security settings.
                    </p>
                </div>
                
                <div className="flex flex-col gap-6 border border-border p-4 rounded-md">
                    <div className="flex flex-col">
                        <h3 className="text-lg font-semibold text-foreground">
                            User details
                        </h3>
                        <p className="text-sm text-muted-foreground">
                            View and manage your personal information and contact details.
                        </p>
                    </div>
                    <ModuleBasic module={module} data={{user: this.state.user}} setAlert={this.setAlert} iam={this.iam} />
                </div>

                {showPassword && (
                    <div className="flex flex-col gap-6 border border-border p-4 rounded-md">
                        <div className="flex flex-col">
                            <h3 className="text-lg font-semibold text-foreground">
                                Update your password
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Change your password to keep your account secure.
                            </p>
                        </div>
                        <ModulePassword module={module} data={{user: this.state.user}} setAlert={this.setAlert} iam={this.iam} />
                    </div>
                )}
            </div>
        );
    }

    renderModule(module) {
        const ModuleComponent = module.component;

        const getDescription = () => {
            switch(module.key) {
                case 'TwoFactor':
                    return 'Add an extra layer of security to your account by enabling two-factor authentication.';
                case 'ActiveSessions':
                    return 'Manage and monitor your active login sessions across different devices.';
                case 'Activity':
                    return 'Review your account activity and login history.';
                case 'ApiTokens':
                    return 'Create and manage API tokens for programmatic access to your account.';
                default:
                    return '';
            }
        };

        return (
            <div key={module.key} id={'module-' + module.key} className="flex flex-col gap-6">
                <div className="flex flex-col">
                    <h2 className="text-2xl font-semibold text-foreground">
                        {module.title}
                    </h2>
                    {getDescription() && (
                        <p className="text-sm text-muted-foreground">
                            {getDescription()}
                        </p>
                    )}
                </div>
                
                <div className="flex flex-col gap-6 border border-border p-4 rounded-md">
                    <ModuleComponent module={module} data={{user: this.state.user}} setAlert={this.setAlert} iam={this.iam} />
                </div>
            </div>
        )
    }

    renderAlert(message = null) {
        if (message) {
            return (
                <GeckoAlert type={APP_CONST.ALERT_TYPE_WARNING}>
                    {message}
                </GeckoAlert>
            )
        }

        return null;
    }

    render() {
        return (
            <div className="flex min-h-[calc(100vh-57px)]">
                <div className="sticky top-[57px] h-[calc(100vh-57px)] overflow-y-auto w-64 shrink-0 border-r border-border py-8 pr-6">
                    {this.renderNavigation()}
                </div>
                <div className="flex-1 min-w-0 py-8 pl-6 pr-0">
                    {this.renderAlert(this.state.alert)}
                    {this.renderActiveModule()}
                </div>
            </div>
        );
    }
}

export default AccountPage;
